<template>
  <span
    :class="['badge', `badge-${variant}`, size ? `badge-${size}` : '']"
    :style="customStyle"
  >
    <slot></slot>
  </span>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'info',
    validator: (value) => [
      'demande_devis', 'appel_qualification', 'devis_en_attente', 'devis_signe', 'demande_perdue', 'devis_perdu', 'encode_digiforma', 'corbeille',
      'particulier', 'professionnel',
      'test', 'admin', 'commercial', 'info', 'warning', 'success', 'danger', 'validated', 'secondary',
      // Verbes HTTP de la documentation de l'API : vert pour ce qui lit,
      // bleu pour ce qui écrit.
      'method-get', 'method-post',
      'email_confirmation', 'email_devis_sent', 'email_reminder', 'email_status_update',
      'count-primary', 'count-warning', 'count-success', 'count-danger'
    ].includes(value)
  },
  size: {
    type: String,
    default: null,
    validator: (value) => ['xs', 'sm', 'lg'].includes(value) || value === null
  },
  style: {
    type: Object,
    default: () => ({})
  }
})

const customStyle = computed(() => {
  return props.style
})
</script>

<style scoped>
.badge {
  display: inline-flex;
  align-items: center;
  padding: var(--spacing-1) var(--spacing-3);
  border-radius: var(--radius-full, 9999px);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  line-height: 1.4;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.badge-demande_devis {
  background: #dbeafe;
  color: #1e40af;

}

.badge-appel_qualification {
  background: #fef3c7;
  color: #92400e;

}

.badge-devis_en_attente {
  background: #f3e8ff;
  color: #6b21a8;

}

.badge-devis_signe {
  background: #d1fae5;
  color: #065f46;

}

.badge-demande_perdue {
  background: #ffe4e6;
  color: #9f1239;

}

.badge-devis_perdu {
  background: #fee2e2;
  color: #991b1b;

}

.badge-encode_digiforma {
  background: #ccfbf1;
  color: #115e59;
}

.badge-corbeille {
  background: #f3f4f6;
  color: #374151;

}

.badge-particulier {
  background: #f3e8ff;
  color: #6b21a8;

}

.badge-professionnel {
  background: #d1fae5;
  color: #065f46;

}

/* Troisième type d'entreprise, posé par le tunnel d'inscription. Il lui fallait
   sa propre teinte : rendu en « prestataire externe », il devenait invisible —
   et la fiche finissait par l'y convertir pour de bon. */
.badge-entreprise_cliente {
  background: #f3e8ff;
  color: #6b21a8;
}

.badge-test {
  background: #fef3c7;
  color: #92400e;

  margin-left: var(--spacing-sm);
  font-size: 0.65rem;
  padding: 2px 8px;
}

.badge-admin {
  background: #dbeafe;
  color: #1e40af;

}

.badge-commercial {
  background: #ccfbf1;
  color: #115e59;

}

.badge-info {
  background: #dbeafe;
  color: #1e40af;

}

.badge-warning {
  background: #fef3c7;
  color: #92400e;

}

.badge-success {
  background: #d1fae5;
  color: #065f46;
}

.badge-danger {
  background: #fee2e2;
  color: #991b1b;
}

.badge-validated {
  background: #d1fae5;
  color: #065f46;

  gap: 4px;
  cursor: help;
}

.badge-secondary {
  background: #f3f4f6;
  color: #6b7280;

}

/* Email type variants */
.badge-email_confirmation {
  background: #d1fae5;
  color: #065f46;

}

.badge-email_devis_sent {
  background: #dbeafe;
  color: #1e40af;

}

.badge-email_reminder {
  background: #fef3c7;
  color: #92400e;

}

.badge-email_status_update {
  background: #f3e8ff;
  color: #6b21a8;

}

.badge-validated :deep(i:last-child) {
  font-size: 0.6rem;
  margin-left: -2px;
}

/* Count pill variants — pastille pleine compacte (compteurs) */
.badge-count-primary,
.badge-count-warning,
.badge-count-success,
.badge-count-danger {
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 var(--spacing-1);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  border-radius: var(--radius-full);
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  color: var(--color-text-inverse);
}

.badge-count-primary { background: var(--color-primary); }
.badge-count-warning { background: var(--color-warning); }
.badge-count-success { background: var(--color-success); }
.badge-count-danger { background: var(--color-danger); }

/* Verbes HTTP — documentation de l'API. Le verbe est un mot technique qu'on
   lit à la lettre : il garde la fonte à chasse fixe du chemin qu'il précède. */
.badge-method-get,
.badge-method-post {
  font-family: var(--font-family-mono);
  letter-spacing: 0.04em;
}

.badge-method-get {
  background: var(--color-success-lighter);
  color: var(--color-success-darker);
}

.badge-method-post {
  background: var(--color-info-lighter);
  color: var(--color-info-darker);
}

/* Size variants */
.badge-xs {
  padding: 0 var(--spacing-1);
  height: 18px;
  font-size: var(--font-size-xs);
}

.badge-sm {
  padding: 2px 8px;
  font-size: 0.65rem;
}

.badge-lg {
  padding: 6px 16px;
  font-size: 0.875rem;
}
</style>

